
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";
import { Button } from "@/components/ui/button";
import { Maximize, Trash2, Edit2 } from "lucide-react";
import { GalleryPhoto } from "./types";

interface PhotoCardProps {
  photo: GalleryPhoto;
  onDeleteClick: (photo: GalleryPhoto) => void;
  onFullscreenClick: (photoUrl: string) => void;
  onEditClick: (photo: GalleryPhoto) => void;
}

export function PhotoCard({ 
  photo, 
  onDeleteClick, 
  onFullscreenClick, 
  onEditClick 
}: PhotoCardProps) {
  const { language } = useLanguage();

  return (
    <Card key={photo.id} className="overflow-hidden">
      <CardContent className="p-0">
        <div className="relative">
          <img 
            src={photo.url} 
            alt={photo.title || 'Gallery photo'} 
            className="w-full h-48 object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all flex items-center justify-center opacity-0 hover:opacity-100">
            <Button 
              variant="secondary" 
              size="icon" 
              className="mx-1"
              onClick={() => onFullscreenClick(photo.url)}
            >
              <Maximize className="h-4 w-4" />
            </Button>
            <Button 
              variant="secondary" 
              size="icon" 
              className="mx-1"
              onClick={() => onEditClick(photo)}
            >
              <Edit2 className="h-4 w-4" />
            </Button>
            <Button 
              variant="destructive" 
              size="icon" 
              className="mx-1"
              onClick={() => onDeleteClick(photo)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="p-4">
          <h3 className="text-sm font-medium truncate">
            {photo.title || t('gallery.untitledPhoto', language) || 'Untitled Photo'}
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            {new Date(photo.created_at).toLocaleDateString()}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
